import React, { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";

import { addTemporaryToast } from "shared/components/Toast";
import { IAPIState, IStatus } from "shared/types";
import { ConfirmModal } from "shared/components";
import { copy } from "shared/assets";

import { dataMetaSelector, revokeBandInvitation } from "Bands/state";
import { BAND_ACTIONS, IBandInvitation } from "Bands/types";
import { useClearActionMetaOnUnMount } from "Bands/utils";
import { User } from "shared/assets/icons";
import { useBandContext, useBandInvitationContext } from "Bands/contexts";

const revokeInvitationCopy = copy.members.revokeInvitation;

interface RevokeBandInvitationModalProps {
  invitation: IBandInvitation;
  handleClose: () => void;
}

export const RevokeBandInvitationModal: React.FC<RevokeBandInvitationModalProps> = ({
  invitation,
  handleClose,
}) => {
  const dispatch = useDispatch();

  const { api: apiMeta } = useSelector(dataMetaSelector(BAND_ACTIONS.revokeBandInvitation));

  const { bandId, band } = useBandContext();
  const { fetchSentInvitations, fetchPendingInvitations } = useBandInvitationContext();

  useClearActionMetaOnUnMount(BAND_ACTIONS.revokeBandInvitation);

  useEffect(() => {
    if (!bandId) return;

    if (apiMeta?.status === IAPIState.succeeded) {
      // refetech pending invitations
      fetchPendingInvitations();

      // refetech sent invitations
      fetchSentInvitations();

      handleClose();
      dispatch(
        addTemporaryToast({
          status: IStatus.success,
          message: revokeInvitationCopy.messages.success,
        })
      );
    }
  }, [dispatch, apiMeta, handleClose, bandId, fetchSentInvitations, fetchPendingInvitations]);

  const handleConfirm = () => {
    if (invitation) {
      dispatch(revokeBandInvitation(invitation.id));
    }
  };

  const message = useMemo(
    () =>
      revokeInvitationCopy.messages.modal
        .replace("%email", invitation.email)
        .replace("%band", band?.title!)
        .toString(),
    [invitation.email, band?.title]
  );

  return (
    <ConfirmModal
      title={revokeInvitationCopy.title}
      icon={User}
      message={message}
      confirmButtonCopy={revokeInvitationCopy.buttons.primary}
      handleConfirm={handleConfirm}
      handleClose={handleClose}
      apiMeta={apiMeta}
    />
  );
};
